<template>
  <div v-if="showTargetEditor" class="space-y-2">
    <div v-if="showEntityTarget">
      <label class="mb-1 block text-xs text-gray-400">Entity target</label>
      <EntitySelector
        :modelValue="modelValue"
        :settings="settings"
        :domain="entityDomain"
        placeholder="entity.id"
        @update:modelValue="onEntity" />
    </div>

    <div v-if="showDeviceTarget">
      <label class="mb-1 block text-xs text-gray-400">Device target</label>
      <HassSelector
        :modelValue="targetValue('device_id')"
        :settings="settings"
        placeholder="device_id"
        :selector="{ kind: 'device', preserveUnknown: true }"
        @update:modelValue="setTargetField('device_id', $event)" />
    </div>

    <div v-if="showAreaTarget">
      <label class="mb-1 block text-xs text-gray-400">Area target</label>
      <HassSelector
        :modelValue="targetValue('area_id')"
        :settings="settings"
        placeholder="area_id"
        :selector="{ kind: 'area', preserveUnknown: true }"
        @update:modelValue="setTargetField('area_id', $event)" />
    </div>
    <p v-if="!serviceKnown && hasPreservedTarget" class="text-xs text-amber-400">
      Target metadata unavailable; the saved target is preserved.
    </p>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import HassSelector from '../HassSelector.vue';
import EntitySelector from '../EntitySelector.vue';

const props = defineProps({
  modelValue: { type: String, default: '' },
  target: { type: Object, default: () => ({}) },
  service: { type: Object, default: null },
  settings: { type: Object, required: true },
});

const emit = defineEmits(['update:modelValue', 'update:target']);

const targetMetadata = computed(() => props.service?.target ?? {});
const serviceKnown = computed(() => Boolean(props.service));
const hasPreservedTarget = computed(() => {
  const target = props.target ?? {};
  const hasTargetValue = Object.entries(target).some(([key, value]) =>
    key !== 'source' && value !== '' && value != null && (!Array.isArray(value) || value.length > 0),
  );
  return hasTargetValue || Boolean(String(props.modelValue ?? '').trim());
});

const entitySelector = computed(() => {
  const config = targetMetadata.value.entity ?? {};
  const domain = config.domain ?? config.domains ?? '';
  return {
    kind: 'entity',
    filters: { domain },
    preserveUnknown: true,
  };
});

const entityDomain = computed(() => {
  const domain = entitySelector.value.filters.domain;
  return Array.isArray(domain) ? domain[0] ?? '' : domain;
});

const showEntityTarget = computed(() => supportsTarget('entity') || (!serviceKnown.value && hasPreservedTarget.value));
const showDeviceTarget = computed(() => supportsTarget('device'));
const showAreaTarget = computed(() => supportsTarget('area'));
const showTargetEditor = computed(() => showEntityTarget.value || showDeviceTarget.value || showAreaTarget.value);

function supportsTarget(kind) {
  return targetMetadata.value?.[kind] !== undefined ||
    targetMetadata.value?.[`${kind}_id`] !== undefined;
}

function targetValue(key) {
  const value = props.target?.[key];
  if (Array.isArray(value)) return value.join(', ');
  return value ?? '';
}

function onEntity(value) {
  emit('update:modelValue', value);
}

function setTargetField(key, value) {
  emit('update:target', { ...(props.target ?? {}), [key]: value });
}
</script>
